import { Service } from '@angular/core';

import { BehaviorSubject } from 'rxjs';

import type { Preferences } from 'src/app/models/preferences';

/**
 * Handles user preferences.
 */
@Service()
export class PreferencesService {
	private readonly defaultPreference: Preferences = { staging: false };

	private preferencesSubject = new BehaviorSubject<Preferences>(this.getPreferences());

	public preferences$ = this.preferencesSubject.asObservable();

	/**
	 * Toggle staging. Used for GraphQL requests to either staging or release
	 */
	public toggleStaging() {
		this.updatePreferences({ staging: !this.preferencesSubject.value.staging });
	}

	/**
	 * Updates local storage for persistant usage of preferences.
	 * Updates preference subject so subscribers of the preference
	 * observable are updated with latest preferences
	 * @param newPreferences
	 */
	private setPreferences(newPreferences: Preferences) {
		localStorage.setItem('preferences', JSON.stringify(newPreferences));
		this.preferencesSubject.next(newPreferences);
	}

	/**
	 * Enables to update a sub set of the preferences
	 * @param patch set of preferences to update
	 */
	private updatePreferences(patch: Partial<Preferences>) {
		const updatedPreferences: Preferences = { ...this.preferencesSubject.value, ...patch };
		this.setPreferences(updatedPreferences);
	}

	/**
	 * Reads preference from local storage.
	 * If no existing preference is found then the default preference is fetched.
	 * @returns preferences
	 */
	private getPreferences(): Preferences {
		const storedPreferences = localStorage.getItem('preferences');
		let preferences = this.defaultPreference;
		if (storedPreferences) {
			try {
				preferences = { ...preferences, ...JSON.parse(storedPreferences) };
			} catch {
				// ignore
			}
		} else {
			// TODO: if developer default to staging
		}
		return preferences;
	}
}
